import { useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Row } from 'react-bootstrap';
import { DateTime as DT } from 'luxon';
import { useTranslation } from 'react-i18next';
import LeadStatus from '../../Leads/LeadRows/LeadStatus';
import { useSettings } from 'context/SettingsContext';
import LeadEventAttachFile from '../LeadEventModal/LeadEventAttachFile';
import { FilesContainer } from 'modules/files';
import 'glightbox/dist/css/glightbox.min.css';
import * as apiLead from 'api/apiLeads';
import { useToast } from 'context/ToastContext';
import SpinnerBlock from 'components/others/SpinnerBlock';
import EToolTip from 'components/others/EToolTip';
import type { LeadEventType, LeadType } from '@econtractor/types';

interface LeadCardEventProps {
    lead: LeadType;
    event: LeadEventType;
    showLockButton?: boolean;
    fetchLead: () => Promise<void>;
}

const LeadCardEvent = ({ event, showLockButton = true, fetchLead }: LeadCardEventProps) => {
    const { t } = useTranslation(['Leads', 'common']);
    const { showToast } = useToast();
    const { localSettings } = useSettings();
    const listAttach = useMemo(
        () =>
            (event?.eventAttach || '')
                .split(';')
                .map((item) => item.trim())
                .filter((item) => item !== ''),
        [event?.eventAttach],
    );
    const listCloudAttach = useMemo(
        () => (Array.isArray(event?.eventAttachCloud) ? event.eventAttachCloud.filter((item) => item?.publicUrl) : []),
        [event?.eventAttachCloud],
    );
    const [isPublished, setIsPublished] = useState(false);
    const eventDate = DT.fromMillis(event?.eventCreated).toLocaleString(DT.DATE_FULL);
    const eventTime = DT.fromMillis(event?.eventCreated).toLocaleString(DT.TIME_SIMPLE);
    const dirEvent = localSettings?.DIRS?.LeadStatus?.find((l: any) => l.ID == event?.eventStatus);
    const statusStyle = dirEvent?.statusJson?.statusStyle || 'secondary';

    const handleChangePublic = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.currentTarget.blur();
        setIsPublished(true);
        const resPublic = await apiLead.publicEvent(event.ID, !Boolean(event.eventPublic));
        if (!resPublic) showToast(t('common:messError'), 'danger');
        await fetchLead();
        setIsPublished(false);
    };

    useEffect(() => {
        const GLightbox = require('glightbox');
        const lightbox = GLightbox({
            selector: '.glightbox',
        });

        return () => lightbox.destroy();
    }, []);

    return (
        <Card
            className={`lead-event-card ticket-card event-ticket-${event.eventStatus}`}
            id={`event-${event.eventCreated}`}
            style={{
                scrollMarginTop: '130px',
                ['--lead-event-accent' as string]: `var(--bs-${statusStyle})`,
            }}
        >
            <SpinnerBlock show={isPublished} onlyBlur={true} />
            <Card.Body className="lead-event-card__body">
                <Row className="g-3">
                    <Col className="mb-0" sm="auto">
                        <div className="lead-event-card__avatar-wrap">
                            <img
                                className="lead-event-card__avatar"
                                src={event.userPicture || '/imgs/user.png'}
                                alt=""
                                onError={(ev) => ((ev.currentTarget as HTMLImageElement).src = '/imgs/user.png')}
                            />
                        </div>
                    </Col>
                    <Col className="min-w-0">
                        <div className="lead-event-card__header">
                            <div className="lead-event-card__author min-w-0">
                                <div className="lead-event-card__author-name text-truncate">{event.userName}</div>
                                <div className="lead-event-card__author-email text-truncate">{event.userEmail}</div>
                            </div>
                            <div className="lead-event-card__meta">
                                <span className="lead-event-card__date">{eventDate}</span>
                                <span className="lead-event-card__time">{eventTime}</span>
                            </div>
                        </div>

                        {event.eventInfo && (
                            <div className="lead-event-card__section">
                                <div className="lead-event-card__section-label">{t('leadStatus.labelInfo')}</div>
                                <div className="lead-event-card__info event-info">
                                    <div dangerouslySetInnerHTML={{ __html: event.eventInfo }} />
                                </div>
                            </div>
                        )}

                        {(listAttach.length > 0 || listCloudAttach.length > 0) && (
                            <div className="lead-event-card__section">
                                <div className="lead-event-card__section-label">{t('leadStatus.labelAttach')}</div>
                                {listAttach.length > 0 && (
                                    <div className="lead-event-card__attach">
                                        <ul className="list-inline mb-0">
                                            {listAttach.map((attach) => (
                                                <LeadEventAttachFile
                                                    key={attach}
                                                    dataGallery={String(event.eventCreated)}
                                                    fileName={attach}
                                                />
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {listCloudAttach.length > 0 && (
                                    <div className="lead-event-card__cloud-attach">
                                        <FilesContainer
                                            files={listCloudAttach}
                                            scope={{ eventId: Number(event.ID || 0) }}
                                            imageColProps={{ xs: 6, md: 4, lg: 3 }}
                                            canEdit={false}
                                            addButtonLabel=""
                                            uploadingLabel=""
                                            saveFirstMessage=""
                                            errorMessage=""
                                            uploadSuccessMessage=""
                                            onFilesChange={async () => true}
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="lead-event-card__footer">
                            <LeadStatus leadStatus={event.eventStatus} />

                            {showLockButton && (
                                <EToolTip
                                    title={
                                        event.eventPublic
                                            ? t('leadStatus.tooltipEventLock')
                                            : t('leadStatus.tooltipEventUnlock')
                                    }
                                >
                                    <span className="d-inline-block">
                                        <Button
                                            disabled={isPublished}
                                            variant={`light-${event.eventPublic ? 'success' : 'secondary'}`}
                                            className="lead-event-card__lock-btn"
                                            size="sm"
                                            onClick={handleChangePublic}
                                        >
                                            <i className={`bi bi-${event.eventPublic ? 'unlock' : 'lock'}`}></i>
                                        </Button>
                                    </span>
                                </EToolTip>
                            )}
                        </div>
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );
};

export default LeadCardEvent;
